export const STORAGE_1P_KEY = 'location_1p_warehouse'
export const STORAGE_2P_KEY = 'location_2p_warehouse'

export const WAREHOUSE_KEY_MAP = {
  Brisbane: 'brisbane_stock_json',
  Melbourne: 'melbourne_stock_json'
}

const DISPATCH_DURATION_KEY_MAP = {
  Brisbane: 'dispatch_duration_brisbane',
  Melbourne: 'dispatch_duration_melbourne'
}

// Number of days from today within which an 'arrivingsoon' PO date is considered valid.
export const ARRIVINGSOON_WAIT_DAYS = 14

export function isSellable(warehouse) {
  return warehouse?.sellable === true || warehouse?.sellable === 'true'
}

export function getMetafieldKey(warehouseName) {
  return WAREHOUSE_KEY_MAP[warehouseName] || null
}

export function getDispatchDuration(activeWarehouse, callout) {
  const key = DISPATCH_DURATION_KEY_MAP[activeWarehouse] || DISPATCH_DURATION_KEY_MAP.Brisbane
  return callout?.[key] || callout?.dispatch_duration || ''
}

// Returns true for any stock status that should be treated as preorder.
// 'arrivingsoon' is included intentionally — treat it as preorder for UI and cart purposes.
export function isPreorderStatus(status) {
  return status === 'preorder' || status === 'arrivingsoon'
}

export function isExcluded(warehouse) {
  const v = String(warehouse || '').trim()
  return v === '' || v === 'Exclude'
}

// Returns the number of calendar days from now until dateStr. Negative if in the past.
function getDaysUntil(dateStr) {
  if (!dateStr) return Infinity
  const ms = new Date(dateStr).getTime() - Date.now()
  return Math.ceil(ms / (1000 * 60 * 60 * 24))
}

export function parseStockJson(raw) {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw.replace(/&quot;/g, '"')) : raw
    // New shape: direct array e.g. [{"onhandstock":225},{"futurestock":0},...]
    // Old shape: object-wrapped e.g. {"Brisbane Warehouse":[...]}
    const entries = Array.isArray(parsed) ? parsed : Object.values(parsed)[0]
    if (!Array.isArray(entries)) return null
    return Object.assign({}, ...entries)
  } catch {
    return null
  }
}

export function resolveActiveWarehouse(warehouse1P, warehouse2P, variant) {
  if (isExcluded(warehouse1P) && isExcluded(warehouse2P)) return null

  const stock1P = !isExcluded(warehouse1P)
    ? parseStockJson(variant.metafields?.[getMetafieldKey(warehouse1P)])
    : null
  const stock2P = !isExcluded(warehouse2P)
    ? parseStockJson(variant.metafields?.[getMetafieldKey(warehouse2P)])
    : null

  // 1P excluded — use 2P directly
  if (isExcluded(warehouse1P)) return warehouse2P

  const primarySellable = isSellable(stock1P)
  const secondarySellable = isSellable(stock2P)

  if (!primarySellable) {
    if (isExcluded(warehouse2P) || !secondarySellable) return warehouse1P

    // Secondary sellable → prefer instock, then arrivingsoon, then regular preorder
    if (stock2P?.stockstatusmessage === 'instock') return warehouse2P
    if (stock2P?.stockstatusmessage === 'arrivingsoon') return warehouse2P
    if (stock2P?.stockstatusmessage === 'preorder') return warehouse2P

    // Secondary out-of-stock → fall back to primary
    return warehouse1P
  }

  // Primary is sellable:
  //  - instock -> primary
  if (stock1P?.stockstatusmessage === 'instock') return warehouse1P

  //  - preorder/arrivingsoon -> primary always wins; a sellable secondary instock does not override
  if (isPreorderStatus(stock1P?.stockstatusmessage)) return warehouse1P

  //  - primary soldout or no data -> try secondary if sellable and either instock or preorder
  if (!isExcluded(warehouse2P) && secondarySellable) {
    if (stock2P?.stockstatusmessage === 'instock') return warehouse2P
    if (isPreorderStatus(stock2P?.stockstatusmessage)) return warehouse2P
  }

  // Default: return primary
  return warehouse1P
}

/**
 * Derives stock status flags from warehouse stockData + Shopify variant.
 * @param {object|null} stockData - parsed stock metafield object
 * @param {object} variant - Shopify variant object
 * @param {number} lowStockThreshold
 * @returns {{ isPreorder: boolean, isOutOfStock: boolean, isLowStock: boolean, isInStock: boolean }}
 */
export function getStockStatus(stockData, variant, lowStockThreshold) {
  // Out-of-stock is derived first and gates the rest, so the flags stay mutually
  // exclusive. An 'arrivingsoon' warehouse that is not sellable is out of stock,
  // not a preorder — consumers check these flags in different orders and would
  // otherwise each render a different answer for the same variant.
  //
  // With no warehouse stock data (empty brisbane/melbourne stock-json metafield),
  // defer entirely to Shopify's native availability instead of forcing out-of-stock.
  const hasStockData = stockData != null
  const isOutOfStock = hasStockData
    ? (variant.available === false ||
       !isSellable(stockData) ||
       stockData.stockstatusmessage === 'soldout')
    : variant.available === false

  const isPreorder = !isOutOfStock && isPreorderStatus(stockData?.stockstatusmessage)

  const isLowStock =
    !isOutOfStock &&
    stockData?.stockstatusmessage === 'instock' &&
    variant.inventory_quantity > 0 &&
    variant.inventory_quantity <= lowStockThreshold

  const isInStock = !isOutOfStock && stockData?.stockstatusmessage === 'instock'

  return { isPreorder, isOutOfStock, isLowStock, isInStock }
}

export function formatPreorderDate(stockData) {
  if (!isPreorderStatus(stockData?.stockstatusmessage) || !stockData?.earliestpodate) return null
  const tp = window.translate.product
  const earlyLabel = tp?.preOrderEarlyLabel || 'Early'
  const midLabel = tp?.preOrderMidLabel || 'Mid'
  const endLabel = tp?.preOrderEndLabel || 'End of'
  const d = new Date(stockData.earliestpodate)
  const formatted = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })
  const day = parseInt(formatted.split(' ')[0], 10)
  const monthYear = formatted.replace(String(day), '').trim()
  const prefix = day < 11 ? earlyLabel : day <= 20 ? midLabel : endLabel
  return `${prefix} ${monthYear}`
}
